/* @flow */

import { apiService } from 'services/ServiceRegistry';
import { appendQueryParameter } from 'utils/uri';

import endpoints from './endpoints';

type RequestFormType = 'asset' | 'license';
let locationUS: Object = {};
///////////////////////////////////////////////////////////////////////////
// AUTOCOMPLETE
///////////////////////////////////////////////////////////////////////////
export function autoCompleteFetch(
  path: string,
  search: string = '',
  searchFields: string = '',
  id: string = 'id',
  display: string = 'name'
): Promise<Response> {
  // TODO: SEARCH QUERY ON THE BACKEND / PAGINATION ???
  let vPath = path;

  if (search !== '' || searchFields !== '') {
    vPath = appendQueryParameter(vPath, `search=${search}${searchFields}`);
  }

  vPath = appendQueryParameter(vPath, 'page_size=2500');

  return apiService.getResource(vPath);
}

export function autoCompleteFetchParams(
  path: string,
  search: string = '',
  searchFields: string = '',
  id: string = 'id',
  display: string = 'name'
): Promise<*> {
  // TODO: SEARCH QUERY ON THE BACKEND / PAGINATION ???
  return apiService
    .getResource(`${path}&search=${search}${searchFields}`)
    .then((response: Response) => {
      return response;
    });
}

/**
 * Fetch API data by 'id' key
 * @param path - Endpoint path
 * @param id - Id attribute to filter by
 */
export function autoCompleteFetchById(path: string, id: string): Promise<*> {
  return apiService.getResource(`${path}/${id}`).then((response: Response) => {
    return response;
  });
}

export function fetchManufacturers(search: string = '', accountId: ?string): Promise<*> {
  return autoCompleteManufacturers(search, accountId);
}

export function autoCompleteManufacturers(search: string = '', accountId: ?string): Promise<*> {
  const searchFields: string = '';

  if (accountId) {
    return autoCompleteFetch(
      endpoints.manufacturersForUsers + `?account_id=${accountId}`,
      search,
      searchFields
    );
  }

  return autoCompleteFetch(endpoints.manufacturersForUsers, search, searchFields);
}

export function autoCompleteForAllManufacturers(search: string = '') {
  const searchFields: string = '';
  return autoCompleteFetch(endpoints.manufacturers, search, searchFields);
}

export function autoCompleteManufacturersForAccount(
  search: string = '',
  accountId: ?string
): Promise<*> {
  const searchFields: string = '';

  if (accountId) {
    return autoCompleteFetch(
      `${endpoints.manufacturersForAccounts}/${accountId}`,
      search,
      searchFields
    );
  }
}

export function autoCompleteProducts(search: string = '', ManufacturerId: ?string): Promise<*> {
  const searchFields: string = '';

  if (!ManufacturerId) {
    return [];
  }

  let prodFilters = ManufacturerId;

  if (ManufacturerId.charAt(0) === '/') {
    prodFilters = ManufacturerId.substr(1);
  }

  return autoCompleteFetch(
    endpoints.manufacturerProductsForUsers + `/${prodFilters}`,
    search,
    searchFields
  );
}

/**
 * Autocomplete for products based on manufacturer and account
 * @param search
 * @param searchString | Contains both manufacturerId and accountId in the following format - /${manufacturerId}?account_id=${accountId}
 */
export function autoCompleteProductsForManufacturer(
  search: string = '',
  searchString: ?string
): Promise<*> {
  const searchFields: string = '';

  if (!searchString) {
    return [];
  }

  let prodFilters = searchString;

  if (searchString.charAt(0) === '/') {
    prodFilters = searchString.substr(1);
  }

  return autoCompleteFetch(
    endpoints.manufacturerProductsForManufacturer + `/${prodFilters}`,
    search,
    searchFields
  );
}

///////////////////////////////////////////////////////////////////////////
// TRADING ACCOUNTS
///////////////////////////////////////////////////////////////////////////

export function fetchTradingAccounts(search: string = '', regionId: ?string): Promise<*> {
  return autoCompleteTradingAccounts(search, regionId);
}

export function autoCompleteTradingAccounts(search: string = '', regionId: ?string): Promise<*> {
  const searchFields: string = '';

  if (regionId) {
    return autoCompleteFetch(endpoints.accountsDropdown, regionId, searchFields);
  }

  return autoCompleteFetch(endpoints.accountsDropdown, search, searchFields);
}

export function autoCompleteTradingAccountsForBrandAssignment(search: string = '') {
  const uri = window.location.pathname.split('/');
  const id = uri[3];

  if (id) {
    return autoCompleteFetch(endpoints.brandAssignment(id), search);
  }

  return autoCompleteFetch(endpoints.accounts, search, `${search ? ';' : ''}unbranded:true`);
}

export function autoCompleteTradingAccountsForRequestAssetForm(search: string = ''): Promise<*> {
  const searchFields: string = '';

  return autoCompleteFetch(endpoints.requestForm.asset.accounts, search, searchFields);
}

export function autoCompleteTradingAccountsForRushRequestAssetForm(
  search: string = ''
): Promise<*> {
  const searchFields: string = '';

  return autoCompleteFetch(endpoints.requestForm.rushAsset.accounts, search, searchFields);
}

export function autoCompleteTradingAccountsForProductsEditForm(search: string = ''): Promise<*> {
  const searchFields: string = '';
  const url = `${endpoints.requestForm.all.accounts}`;

  return autoCompleteFetch(url, search, searchFields);
}

export function autoCompleteTradingAccountsForRequestLicenseForm(search: string = ''): Promise<*> {
  const searchFields: string = '';

  return autoCompleteFetch(endpoints.requestForm.license.accounts, search, searchFields);
}

///////////////////////////////////////////////////////////////////////////

export function autoCompleteLocation(search: string = ''): Promise<*> {
  const searchFields: string = '';
  return autoCompleteFetch(endpoints.locations, search, searchFields);
}

export function fetchProjects(search: string = ''): Promise<*> {
  return autoCompleteProject(search);
}

export function autoCompleteProject(search: string = ''): Promise<*> {
  const searchFields: string = '';
  return autoCompleteFetch(endpoints.projects, search, searchFields);
}

export function autoCompleteAccountUsers(
  search: string = '',
  tradingAccountId: ?string | ?number
): Promise<*> {
  const searchFields: string = '';

  if (!tradingAccountId) {
    return [];
  }

  // return autoCompleteFetch(endpoints.users + '/account/' + String(tradingAccountId), search, searchFields);
  return autoCompleteFetch(
    `${endpoints.accounts}/${String(tradingAccountId)}/${endpoints.users}`,
    search,
    searchFields
  );
}

export const autoCompleteAccountCompanies = (
  searchFields: { assetRequestLocation?: string } = {}
) => (search: string = '', AccountId: ?(string | number)): Promise<*> => {
  const _searchFields: string =
    searchFields && searchFields.assetRequestLocation
      ? `asset_request_location:${searchFields.assetRequestLocation}`
      : '';

  if (!AccountId) {
    return [];
  }

  return autoCompleteFetch(
    `${endpoints.accounts}/${AccountId}/${endpoints.companies}`,
    search,
    _searchFields
  );
};

export function autoCompleteAssetRequestAccountCompanies(type) {
  return (search: string = '', AccountId: ?string): Promise<*> => {
    const searchFields: string = `asset_request_location:true;asset_request_location_type:${type}`;

    if (!AccountId) {
      return [];
    }

    return autoCompleteFetch(
      `${endpoints.accounts}/${AccountId}/${endpoints.companies}`,
      search,
      searchFields
    );
  };
}

export function autoCompleteRushAssetRequestAccountCompanies(type) {
  return (search: string = '', AccountId: ?string): Promise<*> => {
    const searchFields: string = `rush_asset_request_location:1;rush_asset_request_location_type:${type}`;

    if (!AccountId) {
      return [];
    }

    return autoCompleteFetch(
      `${endpoints.accounts}/${AccountId}/${endpoints.companies}`,
      search,
      searchFields
    );
  };
}

export function autoCompleteCompanySites(search: string = '', CompanyId: ?string): Promise<*> {
  const searchFields: string = `asset_request_location:true;asset_request_location_type:customer|delivery`;

  if (!CompanyId) {
    return [];
  }

  let prodFilters = CompanyId;

  if (CompanyId.charAt(0) === '/') {
    prodFilters = CompanyId.substr(1);
  }

  return autoCompleteFetch(
    `${endpoints.companies}/${prodFilters}/${endpoints.locations}`,
    search,
    searchFields
  );
}

export function autoCompleteAssetRequestCompanySites(type) {
  return (search: string = '', CompanyId: ?string): Promise<*> => {
    const searchFields: string = `asset_request_location:true;asset_request_location_type:${type}`;

    if (!CompanyId) {
      return [];
    }

    let prodFilters = CompanyId;

    if (CompanyId.charAt(0) === '/') {
      prodFilters = CompanyId.substr(1);
    }

    return autoCompleteFetch(
      `${endpoints.companies}/${prodFilters}/${endpoints.locations}`,
      search,
      searchFields
    );
  };
}

export async function autoCompleteAssetRequestDefaultAddress(
  search: string = '',
  AccountId: ?string
): Promise<*> {
  const searchFields: string = '';

  if (!AccountId) {
    return [];
  }

  const endpoint = endpoints.defaultAddress(AccountId);

  return autoCompleteFetch(endpoint, '', searchFields);
}

/**
 * Fetch API to get countries list
 * @param search - Search param
 */
export function autoCompleteCountries(search: string = ''): Promise<*> {
  const searchFields: string = '';

  return autoCompleteFetch(`${endpoints.countries}`, search, searchFields).then(response => {
    locationUS = response.find(location => location.name === 'United States');
    const sorted = response.sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();

      return aName.localeCompare(bName);
    });

    // making most used countries display first
    const mostUsedCountriesWhitelist = ['US', 'GB'];
    const mostUsedCountries = response.filter(item =>
      mostUsedCountriesWhitelist.includes(item.code)
    );
    const filteredOutMostUsedCountries = sorted.filter(
      item => !mostUsedCountriesWhitelist.includes(item.code)
    );

    return [...mostUsedCountries, ...filteredOutMostUsedCountries];
  });
}

/**
 * Fetch API to get all US States
 */
export function autoCompleteUSStates() {
  if (!!locationUS) {
    return autoCompleteFetchById(endpoints.states, locationUS.id);
  } else {
    return new Promise((resolve, reject) => {
      resolve([]);
    });
  }
}

export function autoCompleteBuildings(search: string = '', extraFilters: ?string): Promise<*> {
  const searchFields: string = '';

  if (!extraFilters) {
    return [];
  }

  return autoCompleteFetchParams(`${endpoints.locations}${extraFilters}`, search, searchFields);
}

export function autoCompleteAccountCostCenter(search: string = '', AccountId: ?string): Promise<*> {
  const searchFields: string = '';

  if (!AccountId) {
    return [];
  }

  const vSearch = `account:${AccountId}`;

  return autoCompleteFetch(endpoints.costCenter, vSearch, searchFields);
}

export function autoCompleteAssetRequestAccountCostCenter(
  search: string = '',
  AccountId: ?string
): Promise<*> {
  const searchFields: string = '';

  if (!AccountId) {
    return [];
  }

  const vSearch = `account:${AccountId};status:Active`;

  return autoCompleteFetch(endpoints.costCenter, vSearch, searchFields);
}

export function autoCompleteAccountProject(search: string = '', AccountId: ?string): Promise<*> {
  const searchFields: string = '';

  if (!AccountId) {
    return [];
  }

  const vSearch = `account:${AccountId}`;

  return autoCompleteFetch(endpoints.projects, vSearch, searchFields);
}

export function autoCompleteAssetRequestAccountProject(
  search: string = '',
  AccountId: ?string
): Promise<*> {
  const searchFields: string = '';

  if (!AccountId) {
    return [];
  }

  const vSearch = `account:${AccountId};status:Active|Scheduled`;

  return autoCompleteFetch(endpoints.projects, vSearch, searchFields);
}

export function autoCompleteParentAssets(search: string = '', AccountId: ?string): Promise<*> {
  const searchFields: string = '';

  if (!AccountId) {
    return [];
  }

  return autoCompleteFetch(
    endpoints.accounts + `/${AccountId}/parent-assets`,
    search,
    searchFields
  );
}

export function autoCompleteLanguages(search: string = ''): Promise<*> {
  const searchFields: string = '';

  return autoCompleteFetch('languages', search, searchFields);
}
export function autoCompleteCurrencies(search: string = ''): Promise<*> {
  const searchFields: string = '';

  return autoCompleteFetch('currencies', search, searchFields);
}
export function autoCompletePriceFormats(search: string = ''): Promise<*> {
  const searchFields: string = '';

  return autoCompleteFetch('price-formats', search, searchFields);
}

export function autoCompleteRegions(search: string = ''): Promise<*> {
  const searchFields: string = '';

  return autoCompleteFetch('regions', search, searchFields);
}
/** Used to fill a Context for certain components */
export async function getRushAssetCatalogue(): Promise<Response> {
  const response = await apiService.getResource(endpoints.rushAssetCatalogue);
  return response;
}

/** Used for the ElectroSelect component */
export function autoCompleteCatalogue(): Promise<*> {
  return autoCompleteFetch(endpoints.rushAssetCatalogue, '', '');
}

export function autoCompleteFinancialAuditors(search: string = ''): Promise<*> {
  return autoCompleteFetch('users/financial-auditors', search);
}

export async function autoCompleteErCompanies(search: string = '') {
  const results = await autoCompleteFetch(endpoints.erCompanies, search);

  // Creating the label here because the ElectroSelect components are horrendous.
  if (results) {
    return results.map(item => {
      item.label = `${item.er_code} - ${item.er_description}`;

      return item;
    });
  }

  return results;
}

/**
 * Returns a source function for an AutoCompleteData instance. If includeCAASOption
 * is true the source function will include the CAAS option that should be
 * available only for US.
 */
export function autoCompleteHelpFor(includeCAASOption: boolean = false) {
  return () => {
    const options = [
      { id: 'Asset Audit', label: 'general:assets.labels.audit' },
      { id: 'Upload My Asset List', label: 'general:assets.labels.upload_list' },
      { id: 'Data Cleaning', label: 'general:labels.data_cleaning' },
      { id: 'Fair Market Asset Valuations', label: 'general:labels.fair_market_valuation' },
      {
        id: 'Consignment Sales of Owned Assets',
        label: 'general:assets.owned_assets.labels.consignment_sales',
      },
      { id: 'Sub-Rent Program', label: 'general:assets.labels.sub_rental_program' },
      {
        id: 'Bluetooth Asset Tracking Solutions',
        label: 'general:assets.labels.bluetooth_tracking',
      },
      { id: 'Remove User', label: 'general:assets.labels.remove_user' },
      { id: 'Other', label: 'general:reasons.options.other' },
    ];

    if (includeCAASOption) {
      // Make sure the id for CAAS matches the value defined in backend at
      // App\Events\Main\ContactSupport::CAAS_HELP
      options.splice(-1, 0, {
        id: 'Calibration as a Service',
        label: 'general:calibration_as_a_service',
      });
    }

    return options;
  };
}

///////////////////////////////////////////////////////////////////////////
// PROJECTS LEAD
///////////////////////////////////////////////////////////////////////////

function getProjectLeads(search: string): Array {
  const data: Array = [{ id: 1, name: 'Lead 1' }, { id: 2, name: 'Lead 2' }];

  return search
    ? data.filter(
        (item: Object): boolean => item.name.toLowerCase().indexOf(search.toLowerCase()) !== -1
      )
    : data;
}

export function autoCompleteProjectLeads(search: string = ''): Promise<*> {
  return new Promise((resolve: Function) => {
    setTimeout(() => {
      resolve(getProjectLeads(search));
    }, 500);
  });
}

///////////////////////////////////////////////////////////////////////////
// PROJECTS STATUS
///////////////////////////////////////////////////////////////////////////

function getProjectStatus(search: string): Array {
  const data: Array = [{ id: 1, name: 'State 1' }, { id: 2, name: 'State 2' }];

  return search
    ? data.filter(
        (item: Object): boolean => item.name.toLowerCase().indexOf(search.toLowerCase()) !== -1
      )
    : data;
}

export function autoCompleteProjectStatus(search: string = ''): Promise<*> {
  return new Promise((resolve: Function) => {
    setTimeout(() => {
      resolve(getProjectStatus(search));
    }, 500);
  });
}

///////////////////////////////////////////////////////////////////////////
// PROJECTS ENGINEERS
///////////////////////////////////////////////////////////////////////////

function getProjectEngineers(search: string): Array {
  const data: Array = [{ id: 1, name: 'Eng 1' }, { id: 2, name: 'Eng 2' }];

  return search
    ? data.filter(
        (item: Object): boolean => item.name.toLowerCase().indexOf(search.toLowerCase()) !== -1
      )
    : data;
}

export function autoCompleteProjectEngineers(search: string = ''): Promise<*> {
  return new Promise((resolve: Function) => {
    setTimeout(() => {
      resolve(getProjectEngineers(search));
    }, 500);
  });
}

///////////////////////////////////////////////////////////////////////////
// PROJECTS COST CENTRE
///////////////////////////////////////////////////////////////////////////

function getProjectCc(search: string): Array {
  const data: Array = [{ id: 1, name: 'Cost 1' }, { id: 2, name: 'Cost 2' }];

  return search
    ? data.filter(
        (item: Object): boolean => item.name.toLowerCase().indexOf(search.toLowerCase()) !== -1
      )
    : data;
}

export function autoCompleteProjectCc(search: string = ''): Promise<*> {
  return new Promise((resolve: Function) => {
    setTimeout(() => {
      resolve(getProjectCc(search));
    }, 500);
  });
}
